/* eslint-disable react-hooks/exhaustive-deps */
import styled from '@emotion/styled';
import * as React from 'react';
import { Spacings } from '@styles/index';
import { ClickAwayListener } from '@material-ui/core';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Save from '@icon/Save';
import Saved from '@icon/Saved';
import ArrowUp from '@icon/Syllabus/ArrowUp';
import H6 from '@base/H6/H6';
import pushRoute from '@utils/pushRoute';
import { saveEntity } from '@actions/saved';
import { savedObjectTypes } from '@constants/index';
import { getEntityData as getItemEntityData } from '@models/Item';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import useFetch from '@hooks/useFetch';
import {
  DOWNLOAD_PDF,
  SAVE_SAVE_CONTENT,
  TOPIC_SYLLABUS_PAGE
} from '@constants/segment';
import { segment } from '@actions/analytics';
import Skeleton from '@material-ui/lab/Skeleton';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { getClassInfoEndpoint } from '@cont/Syllabus/constants/apiHelper';
import { useSWRConfig } from 'swr';
import { ILesson } from 'types/syllabus';
import { getIsGTPGoal } from '@api/selectors/constants';
import Pdf from '@icon/Pdf';
import useConstants from '@api/hooks/useConstants';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';

export interface ILessonMenuPopupContentProps {
  selectedLesson: ILesson;
  closePopup: () => void;
  hidePopupOnSavedClick: () => void;
  onOpenUnsaveModal: () => void;
  handlePostSave: (res: any, isSave?: boolean) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-items: center;
  min-width: 202px;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  background: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: ${Spacings.SPACING_2B};
  height: ${Spacings.SPACING_11B};
`;

const Placeholder = styled(Skeleton)`
  background: var(--color-divider);
`;

const PlaceholderText = styled(Skeleton)`
  background: var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};
`;

const LessonMenuPopupContent: React.FC<ILessonMenuPopupContentProps> = ({
  selectedLesson,
  closePopup,
  hidePopupOnSavedClick,
  onOpenUnsaveModal,
  handlePostSave
}) => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { goalUID, educatorUID, educatorUsername } = query;
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const endpoint = getClassInfoEndpoint(selectedLesson.uid, goalUID, true);
  const entityType = savedObjectTypes.LESSON;
  const isEducatorLibrary = educatorUID && educatorUsername;
  const goalSegmentData = useGoalEventProps({
    goalUID: goalUID as string
  });
  const isRecordedGoal = useIsRecordedGoal(goalUID);

  const { mutate } = useSWRConfig();
  const onSuccess = React.useCallback(
    (data, key) => {
      const item = getItemEntityData(data);
      mutate(
        key,
        {
          ...item,
          programme: item.programme.course
        },
        false
      );
    },
    [mutate]
  );

  const { data: itemDetailsObject, isLoading } = useFetch(
    endpoint,
    {
      headers: {
        'X-Direct-To': 'sheldon',
        'X-PAGE': 'SYLLABUS'
      }
    },
    {
      onSuccess,
      revalidateIfStale: true
    }
  );
  const pdfUrl = itemDetailsObject?.attachments?.notes?.[0]?.url;

  const handleSaveItem = () => {
    dispatch(
      saveEntity({
        goalUid: goalUID,
        objectUid: selectedLesson.uid,
        entityType
      })
    )
      // @ts-ignore
      .then((res) => {
        if (res?.result) {
          mutate(endpoint, { ...itemDetailsObject, isBookmarked: true });
        }
        return handlePostSave(res, true);
      })
      .then(() => {
        segment.track(SAVE_SAVE_CONTENT, {
          last_primary_source: isEducatorLibrary
            ? 'Educator Profile'
            : TOPIC_SYLLABUS_PAGE,
          last_primary_source_section: isEducatorLibrary
            ? 'Educator Profile'
            : 'Topic page - Menu Popup',
          save_content_type: 'Lesson',
          save_content_uid: selectedLesson.uid,
          save_content_title: selectedLesson.title,
          ...goalSegmentData
        });
      });
    closePopup();
  };

  const goToEducatorPage = () => {
    pushRoute('/[authorID]', `/@${selectedLesson.author.username}`);
    closePopup();
  };

  const handlePdfDownload = () => {
    segment.track(DOWNLOAD_PDF, {
      last_primary_source: isEducatorLibrary
        ? 'Educator Profile'
        : TOPIC_SYLLABUS_PAGE,
      last_primary_source_section: isEducatorLibrary
        ? 'Educator Profile'
        : 'Topic page - Menu Popup',
      download_type: 'PDF',
      lesson_uid: selectedLesson.uid,
      lesson_title: selectedLesson.title,
      ...goalSegmentData
    });
    window.open(pdfUrl);
    closePopup();
  };

  const openUnsavedDialog = () => {
    onOpenUnsaveModal();
    hidePopupOnSavedClick();
  };

  const renderMenuItems = () => {
    if (isLoading) {
      return (
        <>
          <MenuItem>
            <Placeholder
              variant="circle"
              height={Spacings.SPACING_5B}
              width={Spacings.SPACING_5B}
            />
            <PlaceholderText variant="text" width="105px" />
          </MenuItem>
          {!isGTP && (
            <MenuItem>
              <Placeholder
                variant="circle"
                height={Spacings.SPACING_5B}
                width={Spacings.SPACING_5B}
              />
              <PlaceholderText variant="text" width="105px" />
            </MenuItem>
          )}
        </>
      );
    }

    const showPdfMenuOption = (isGTP || isRecordedGoal) && pdfUrl;

    return (
      <>
        {itemDetailsObject?.isBookmarked ? (
          <MenuItem onClick={openUnsavedDialog}>
            <Saved size="20" color="var(--color-text-secondary)" />
            <H6>Saved</H6>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleSaveItem}>
            <Save size="20" color="var(--color-text-secondary)" />
            <H6>Save</H6>
          </MenuItem>
        )}
        {showPdfMenuOption && (
          <MenuItem onClick={handlePdfDownload}>
            <Pdf
              size={Spacings.SPACING_5B}
              color="var(--color-text-secondary)"
            />
            <H6>Download PDF</H6>
          </MenuItem>
        )}

        {!isGTP && (
          <MenuItem onClick={goToEducatorPage}>
            <ArrowUp
              size={Spacings.SPACING_5B}
              fill="var(--color-text-secondary)"
            />
            <H6>View educator</H6>
          </MenuItem>
        )}
      </>
    );
  };

  return (
    <>
      <ClickAwayListener onClickAway={closePopup}>
        <Container>{renderMenuItems()}</Container>
      </ClickAwayListener>
    </>
  );
};

LessonMenuPopupContent.displayName = 'LessonMenuPopupContent';
export default LessonMenuPopupContent;
